/**
 * Fuel log — refill entries with cost and consumption stats.
 * Volumes are stored in liters on the backend and shown in the user's
 * preferred fuel unit (trip preferences).
 */
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon, FireIcon } from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import { convertFuel, getFuelUnit, formatFuel } from '../../utils/unitConversions';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 1rem;
`;

const Card = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const PageTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
`;

const StatBox = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1rem;

  .value {
    font-size: 1.4rem;
    font-weight: 700;
    color: ${theme.colors.text.primary};
  }

  .label {
    font-size: 0.8rem;
    color: ${theme.colors.text.secondary};
    margin-top: 2px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
  align-items: end;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};

  input, select {
    padding: 0.5rem 0.65rem;
    border: 1px solid ${theme.colors.border.primary};
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
    font-size: 0.9rem;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${(p) => (p.$primary ? theme.colors.primary.main : theme.colors.border.primary)};
  background: ${(p) => (p.$primary ? theme.colors.primary.main : 'transparent')};
  color: ${(p) => (p.$primary ? 'white' : theme.colors.text.primary)};
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  svg { width: 16px; height: 16px; }
`;

const EntryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th {
    text-align: left;
    color: ${theme.colors.text.secondary};
    font-weight: 600;
    padding: 0.5rem 0.6rem;
    border-bottom: 2px solid ${theme.colors.border.primary};
    white-space: nowrap;
  }

  td {
    padding: 0.55rem 0.6rem;
    border-bottom: 1px solid ${theme.colors.border.secondary};
    color: ${theme.colors.text.primary};
  }

  @media (max-width: 720px) {
    th:nth-child(6), td:nth-child(6),
    th:nth-child(7), td:nth-child(7) { display: none; }
  }
`;

const emptyForm = {
  filled_at: new Date().toISOString().slice(0, 10),
  volume: '',
  price_total: '',
  location: '',
  engine_hours: '',
  notes: '',
  vessel_id: '',
};

export default function FuelLogPage() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [vessels, setVessels] = useState([]);
  const [fuelUnit, setFuelUnit] = useState('liters');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const unitLabel = getFuelUnit(fuelUnit);
  // convertFuel goes liters -> display unit; this is the inverse for input
  const toLiters = (val) => {
    const n = parseFloat(val);
    if (!Number.isFinite(n)) return null;
    const factor = convertFuel(1, fuelUnit) || 1;
    return n / factor;
  };

  const loadAll = async () => {
    try {
      const [entriesResp, statsResp] = await Promise.all([
        api.get('/api/fuel'),
        api.get('/api/fuel/stats'),
      ]);
      setEntries(entriesResp.data.entries || []);
      setStats(statsResp.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load fuel log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    api.get('/api/vessels')
      .then((r) => {
        const list = r.data.vessels || [];
        setVessels(list);
        if (list.length === 1) setForm((f) => ({ ...f, vessel_id: String(list[0].id) }));
      })
      .catch(() => {});
    api.get('/api/trip-preferences')
      .then((r) => {
        const unit = r.data?.preferences?.fuel_unit;
        if (unit) setFuelUnit(unit);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addEntry = async () => {
    const liters = toLiters(form.volume);
    if (!liters || liters <= 0) {
      toast.error(`Enter the volume in ${unitLabel}`);
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/fuel', {
        filled_at: form.filled_at,
        liters,
        price_total: form.price_total !== '' ? parseFloat(form.price_total) : null,
        location: form.location || null,
        engine_hours: form.engine_hours !== '' ? parseFloat(form.engine_hours) : null,
        notes: form.notes || null,
        vessel_id: form.vessel_id ? parseInt(form.vessel_id, 10) : null,
      });
      toast.success('Fuel entry added');
      setForm((f) => ({ ...emptyForm, vessel_id: f.vessel_id }));
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add fuel entry');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this fuel entry?')) return;
    try {
      await api.delete(`/api/fuel/${id}`);
      toast.success('Entry deleted');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete entry');
    }
  };

  const statCards = useMemo(() => {
    if (!stats) return [];
    const cards = [
      { value: stats.fillCount ?? 0, label: 'Fill-ups' },
      { value: formatFuel(stats.totalLiters || 0, fuelUnit), label: 'Total fuel' },
      { value: `$${(stats.totalCost || 0).toFixed(2)}`, label: 'Total cost' },
    ];
    if (stats.avgPricePerLiter != null) {
      const perUnit = stats.avgPricePerLiter / (convertFuel(1, fuelUnit) || 1);
      cards.push({ value: `$${perUnit.toFixed(3)}/${unitLabel}`, label: 'Avg price' });
    }
    if (stats.litersPerHour != null) {
      cards.push({
        value: `${convertFuel(stats.litersPerHour, fuelUnit).toFixed(1)} ${unitLabel}/h`,
        label: 'Consumption (engine hrs)',
      });
    }
    return cards;
  }, [stats, fuelUnit, unitLabel]);

  return (
    <Container>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <PageTitle>
            <FireIcon style={{ width: 26, height: 26 }} />
            Fuel Log
          </PageTitle>
          <span style={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
            Showing volumes in {unitLabel} (change in Settings → Trip Preferences)
          </span>
        </div>
      </Card>

      {statCards.length > 0 && (
        <Card>
          <StatGrid>
            {statCards.map((s, i) => (
              <StatBox key={i}>
                <div className="value">{s.value}</div>
                <div className="label">{s.label}</div>
              </StatBox>
            ))}
          </StatGrid>
        </Card>
      )}

      <Card>
        <h3 style={{ marginTop: 0, color: theme.colors.text.primary }}>Add fill-up</h3>
        <FormGrid>
          <Field>
            Date
            <input type="date" value={form.filled_at} onChange={(e) => setForm({ ...form, filled_at: e.target.value })} />
          </Field>
          <Field>
            Volume ({unitLabel})
            <input type="number" min="0" step="0.1" placeholder="0.0" value={form.volume} onChange={(e) => setForm({ ...form, volume: e.target.value })} />
          </Field>
          <Field>
            Total price ($)
            <input type="number" min="0" step="0.01" placeholder="optional" value={form.price_total} onChange={(e) => setForm({ ...form, price_total: e.target.value })} />
          </Field>
          <Field>
            Location
            <input type="text" placeholder="e.g. Buckhorn Marina" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </Field>
          <Field>
            Engine hours
            <input type="number" min="0" step="0.1" placeholder="optional" value={form.engine_hours} onChange={(e) => setForm({ ...form, engine_hours: e.target.value })} />
          </Field>
          {vessels.length > 1 && (
            <Field>
              Vessel
              <select value={form.vessel_id} onChange={(e) => setForm({ ...form, vessel_id: e.target.value })}>
                <option value="">—</option>
                {vessels.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </Field>
          )}
          <Field>
            Notes
            <input type="text" placeholder="optional" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <Button $primary onClick={addEntry} disabled={saving}>
            <PlusIcon />
            {saving ? 'Saving…' : 'Add entry'}
          </Button>
        </FormGrid>
      </Card>

      <Card>
        <h3 style={{ marginTop: 0, color: theme.colors.text.primary }}>History</h3>
        {loading ? (
          <p style={{ color: theme.colors.text.secondary }}>Loading…</p>
        ) : entries.length === 0 ? (
          <p style={{ color: theme.colors.text.secondary }}>No fuel entries yet — add your first fill-up above.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <EntryTable>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Volume</th>
                  <th>Price</th>
                  <th>$/{unitLabel}</th>
                  <th>Location</th>
                  <th>Engine hrs</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const liters = parseFloat(e.liters);
                  const price = e.price_total != null ? parseFloat(e.price_total) : null;
                  const displayVol = convertFuel(liters, fuelUnit);
                  return (
                    <tr key={e.id}>
                      <td>{new Date(e.filled_at).toLocaleDateString()}</td>
                      <td>{displayVol.toFixed(1)} {unitLabel}</td>
                      <td>{price != null ? `$${price.toFixed(2)}` : '—'}</td>
                      <td>{price != null && displayVol > 0 ? `$${(price / displayVol).toFixed(3)}` : '—'}</td>
                      <td>{e.location || '—'}</td>
                      <td>{e.engine_hours != null ? parseFloat(e.engine_hours).toFixed(1) : '—'}</td>
                      <td>{e.notes || '—'}</td>
                      <td>
                        <Button onClick={() => deleteEntry(e.id)} title="Delete entry">
                          <TrashIcon />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </EntryTable>
          </div>
        )}
      </Card>
    </Container>
  );
}
